import { GetStaticPaths, GetStaticProps } from 'next';

import { getServerPageGetPermitDetail, getServerPageGetPermitSlugs } from 'api/graphql-helper';
import client, { httpLink, setPreviewSecret } from 'api/apollo-client';
import {
	Enum_Componentinternaldefaultmoduleoptions_Backgroundcolor,
	PageInformationFieldsFragment,
	PermitDetailSingleFieldsFragment,
	PermitFieldsFragment
} from 'api/graphql-types';

import Layout from 'modules/layout/layout';
import { ModuleBuilder } from 'modules/module-builder/module-builder';
import { Head } from 'modules/head/head';
import IntroHeader from 'modules/intro-header/intro-header';

import { getStrapiParametersFromContext } from 'utils/strapi-parameters';
import { IBasePageProps } from 'types/page';
import usePageProtection, { IUsePageProtectionVariables } from 'utils/hooks/use-page-protection';
import PasswordLayout from 'modules/password-layout';
import { buildPasswordProtectionStaticProps } from 'utils/password-protection-static';
import { BaseModule } from 'modules/base-module/base-module';
import { useRouter } from 'next/router';
import { useIntl } from 'react-intl';
import { getFormattedDate } from 'utils/date';
import clsx from 'clsx';
import styles from './vergunningen.module.scss';
import { ROUTES } from 'constants/routes';
import { buildSlugPaths } from 'utils/build-slug-paths';

interface IPermitDetailPageProps extends IBasePageProps {
	pageInformation?: PageInformationFieldsFragment;
	permitBySlug?: PermitFieldsFragment;
	permitDetail?: PermitDetailSingleFieldsFragment;
	id?: string;
	variables?: IUsePageProtectionVariables;
	isPasswordProtected?: boolean;
	passwordPageLayout?: any;
}

const PermitDetailPage = (props: IPermitDetailPageProps): React.JSX.Element => {
	const { page, PasswordModule } = usePageProtection({
		pageData: props.permitBySlug || null,
		pageQuery: () => getServerPageGetPermitDetail({ variables: props.variables }, client)
	});
	const router = useRouter();
	const { locale } = router;
	const intl = useIntl();

	const formattedDate = props.permitBySlug?.updatedAt
		? getFormattedDate(new Date(props.permitBySlug?.updatedAt as string), 'd-MMMM-y', locale)
		: '';
	const modules = page?.modules || [];
	const globalModules = props.permitDetail?.modules || [];
	const slug = page?.slug;

	return (
		<Layout
			contentId={props?.id}
			typeName={page?.__typename}
			navbar={props?.pageInformation?.navbar}
			footer={props?.pageInformation?.footer}
		>
			<Head seoModule={page?.seo} seoParent={props?.pageInformation?.seo} />
			{page && page.__typename === 'Permit' ? (
				<>
					<BaseModule
						defaultModuleOptions={{
							backgroundColor: page?.header
								?.backgroundColor as Enum_Componentinternaldefaultmoduleoptions_Backgroundcolor,
							padding: 'none'
						}}
					>
						<IntroHeader
							module={{
								title: page?.header?.title || '',
								backgroundColor: page?.header?.backgroundColor,
								excerpt: page?.header?.excerpt || '',
								cover: page?.header?.cover
							}}
							slug={page.slug}
						/>
					</BaseModule>
					<BaseModule>
						<div className={styles.updatedAt}>
							{intl.formatMessage({ id: 'modules.bodyText.updatedAt' })}: {formattedDate}
						</div>
					</BaseModule>
					<BaseModule
						defaultModuleOptions={{
							backgroundColor: 'white',
							padding: 'medium'
						}}
					>
						<div className={styles.wrapper}>
							<div className={clsx(styles.permits)}>
								<span className={styles.title}>{intl.formatMessage({ id: 'modules.permits.law' })}: </span>
								<br></br>
								{props.permitBySlug?.permitLaws &&
									props.permitBySlug.permitLaws.map((permit) => (
										<div key={permit.data.documentId}>
											<span>{permit.data.attributes?.name}</span>
											<br></br>
										</div>
									))}
							</div>
						</div>
					</BaseModule>
					<ModuleBuilder modules={[...modules, ...globalModules]} slug={slug || '/'} />
				</>
			) : props.isPasswordProtected ? (
				<PasswordLayout
					locale={props.variables?.locale}
					slug={props.variables?.slug}
					pageData={props.passwordPageLayout}
				>
					<PasswordModule />
				</PasswordLayout>
			) : null}
		</Layout>
	);
};

export const getStaticPaths: GetStaticPaths = async (ctx) => {
	const result = await getServerPageGetPermitSlugs({}, client);
	const slugs = result.props.data.findAllSlugs;

	return {
		paths: buildSlugPaths(slugs, ROUTES.PERMITS.slug, ctx.defaultLocale),
		fallback: 'blocking'
	};
};

export const getStaticProps: GetStaticProps = async (ctx) => {
	try {
		const { locale, draftMode, publicationState } = getStrapiParametersFromContext(ctx);
		const slug = ctx.params?.slug ? `/${ROUTES.PERMITS.slug}/${ctx.params.slug}` : '/';
		const variables = {
			slug,
			locale,
			publicationState
		};

		if (publicationState === 'DRAFT') {
			client.setLink(setPreviewSecret(process.env.STRAPI_PREVIEW_SECRET as string).concat(httpLink));
		}

		const passwordResult = await buildPasswordProtectionStaticProps<any, any, any>({
			fullSlug: slug,
			locale,
			publicationState,
			draftMode,
			contentTypeUid: 'api::permit.permit',
			contentTypeName: 'permit',
			fetchPage: () => getServerPageGetPermitDetail({ variables }, client),
			extractInformation: (data) => data?.props?.data?.pageInformation?.data?.attributes,
			extractPage: (data) => {
				const node = data?.props?.data?.permitBySlug?.data;
				return node ? { documentId: node.documentId, attributes: node.attributes } : null;
			},
			neutralStyle: false,
			isSubsite: false
		});

		if (!passwordResult.isPasswordProtected && !passwordResult.page) {
			return { notFound: true };
		}

		let permitDetailAttributes: any = null;
		if (!passwordResult.isPasswordProtected) {
			// Re-fetch detail to extract global modules specifics (could be optimized inside utility later)
			const detailResult = await getServerPageGetPermitDetail({ variables }, client);
			permitDetailAttributes = detailResult?.props?.data?.permitDetail?.data?.attributes;
		}

		return {
			props: {
				id: passwordResult.page?.documentId || null,
				variables,
				pageInformation: passwordResult.pageInformation,
				permitBySlug: passwordResult.page,
				permitDetail: permitDetailAttributes,
				isPasswordProtected: passwordResult.isPasswordProtected,
				passwordPageLayout: passwordResult.passwordPageLayout,
				initialReduxState: passwordResult.initialReduxState
			},
			revalidate: Number(process.env.NEXT_PUBLIC_REVALIDATE_TIME_IN_SECONDS)
		};
	} catch (error: any) {
		// eslint-disable-next-line no-console
		console.error('Error in permit detail', error);

		return {
			notFound: true
		};
	}
};

export default PermitDetailPage;
